'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { Header } from '@/components/design/layout/Header';
import { Footer } from '@/components/design/layout/Footer';
import { useRegistrationStore } from '@/state/stores/registrationStore';
import { LogIn, UserCircle, ArrowRight, CircleHelp } from 'lucide-react';
import { AppButton, AppForm, AppInput } from '@/components/design/forms';
import { isBackendConfigured } from '@/api/api-client';
import { loginAztangofestParticipant } from '@/api/aztangofest/loginAztangofestParticipant';
import { applyBookingSessionMeToStore } from '@/lib/applyBookingSessionMeToStore';
import {
  extractEventBookingIdFromBookingJwt,
  setAzDanceBookingToken,
} from '@/lib/azdanceBookingSession';
import { formatApiError } from '@/lib/apiErrorMessage';
import { WhatsAppSupportFab } from '@/components/design/shared/WhatsAppSupportFab';
import { bumpRegistrationMaxStepClient } from '@/lib/registrationFlowCookie';
import { trackCtaClick, trackFrontendError, trackFunnelStep } from '@/lib/telemetry/yandex';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type LoginPageProps = {
  embedded?: boolean;
  onLoginSuccess?: () => void;
  redirectOnSuccess?: boolean;
};

function resolveSafeRedirectPath(rawRedirect: string | null): string {
  if (!rawRedirect) return '/dashboard';
  const redirect = rawRedirect.trim();
  if (!redirect.startsWith('/')) return '/dashboard';
  if (redirect.startsWith('//')) return '/dashboard';
  if (redirect.includes('\\')) return '/dashboard';
  if (/[\r\n\t]/.test(redirect)) return '/dashboard';
  return redirect;
}

export function LoginPage({
  embedded = false,
  onLoginSuccess,
  redirectOnSuccess = true,
}: LoginPageProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const language = typeof locale === 'string' && locale.length > 0 ? locale : 'en';
  const t = useTranslations('strings.loginPage');
  const {
    registeredParticipants,
    currentUser,
    setCurrentUser,
    setCabinetOwnerParticipantId,
    setDashboardEventBookingId,
  } = useRegistrationStore();
  const safeRedirectPath = resolveSafeRedirectPath(searchParams?.get('redirect') ?? null);

  const [loginValue, setLoginValue] = useState(searchParams?.get('id') ?? '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isModalVariant = embedded;

  // Prefill identifier from `?id=` (e.g. admin "open guest cabinet" link).
  useEffect(() => {
    const presetId = searchParams?.get('id');
    if (presetId) setLoginValue(presetId);
  }, [searchParams]);

  useEffect(() => {
    trackFunnelStep('login', 'view', { embedded: embedded ? 1 : 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!loginValue.trim()) {
      trackFrontendError('login_submit', 'empty_identifier');
      setError(t('errorRequired'));
      return;
    }
    trackCtaClick('login_submit', { placement: 'login_page' });

    if (isBackendConfigured()) {
      setSubmitting(true);
      try {
        const res = await loginAztangofestParticipant(loginValue.trim());
        setAzDanceBookingToken(res.accessToken);
        const eidFromJwt =
          extractEventBookingIdFromBookingJwt(res.accessToken.trim()) ?? null;
        setDashboardEventBookingId(eidFromJwt);
        if (!currentUser) {
          setCabinetOwnerParticipantId(null);
          setCurrentUser({
            id: res.id,
            firstName: '',
            lastName: '',
            age: undefined,
            email: '',
            phone: '',
            location: '',
            registeredAt: new Date().toISOString(),
          });
        } else {
          setCurrentUser({
            ...currentUser,
            id: res.id,
          });
        }
        await applyBookingSessionMeToStore(language);
        bumpRegistrationMaxStepClient(6);
        trackCtaClick('login_success_redirect_dashboard', { placement: 'login_page' });
        onLoginSuccess?.();
        if (redirectOnSuccess) {
          router.push(safeRedirectPath);
        }
      } catch (err) {
        const msg = formatApiError(err);
        trackFrontendError('login_backend_failed', err);
        setError(msg || t('errorNotFound'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const foundUser = registeredParticipants.find(
      (p) =>
        p.phone === loginValue.trim() ||
        p.email.toLowerCase() === loginValue.toLowerCase().trim() ||
        p.id === loginValue.trim(),
    );

    if (foundUser) {
      if (!currentUser) {
        setCabinetOwnerParticipantId(null);
        setCurrentUser({
          id: foundUser.id,
          firstName: foundUser.firstName,
          lastName: foundUser.lastName,
          age: foundUser.age,
          email: foundUser.email,
          phone: foundUser.phone,
          country: foundUser.country,
          city: foundUser.city,
          location: foundUser.location,
          gender: foundUser.gender,
          role: foundUser.role,
          level: foundUser.level,
          contactType: foundUser.contactType,
          contactValue: foundUser.contactValue,
          instagram: foundUser.instagram,
          registeredAt: foundUser.registeredAt || new Date().toISOString(),
        });
      }
      trackCtaClick('login_success_redirect_dashboard', { placement: 'login_page' });
      onLoginSuccess?.();
      if (redirectOnSuccess) {
        router.push(safeRedirectPath);
      }
    } else {
      trackFrontendError('login_local_not_found', 'participant_not_found');
      setError(t('errorNotFound'));
    }
  };

  const content = (
    <main
      className={`flex flex-1 items-center justify-center px-4 ${
        isModalVariant ? 'py-6 sm:py-8' : 'py-20'
      }`}
    >
      <div className={`w-full ${isModalVariant ? 'max-w-lg' : 'max-w-md'}`}>
        {/* Glass Card */}
        <div
          className={`backdrop-blur-xl rounded-3xl shadow-2xl ${
            isModalVariant
              ? 'border border-white/15 bg-[#22242b]/95 p-6 sm:p-7'
              : 'border border-white/10 bg-white/5 p-8'
          }`}
        >
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-fest-gold to-fest-gold-mid-b flex items-center justify-center">
              <UserCircle className="w-10 h-10 text-white" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-center mb-2 text-white">{t('title')}</h1>
          <p className={`mb-8 text-center ${isModalVariant ? 'text-gray-300' : 'text-gray-400'}`}>
            {t('subtitle')}
          </p>

          <AppForm onSubmit={handleLogin} className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-300">
                <label htmlFor="loginValue">{t('identifierLabel')}</label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('idHint')}
                      className="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full text-fest-gold/85 transition-colors hover:text-fest-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-fest-gold/60"
                    >
                      <CircleHelp className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={8}
                    className="max-w-72 border border-white/20 bg-[#121316] px-3 py-2 text-center text-xs leading-relaxed text-white shadow-2xl"
                  >
                    {t('idHint')}
                  </TooltipContent>
                </Tooltip>
              </div>
              <AppInput
                type="text"
                id="loginValue"
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
                placeholder={t('identifierPlaceholder')}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-fest-gold focus:border-transparent transition-all"
              />
              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
            </div>

            <AppButton
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-fest-gold to-fest-gold-mid-b text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-fest-gold/50 transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              {t('submit')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </AppButton>
          </AppForm>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 text-gray-400">{t('dividerOr')}</span>
            </div>
          </div>

          <AppButton
            onClick={() => {
              trackCtaClick('login_create_profile', { placement: 'login_page' });
              router.push('/profile-registration');
            }}
            className="w-full py-3 bg-white/5 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <LogIn className="w-5 h-5" />
            {t('createProfile')}
          </AppButton>

          <p className={`mt-6 text-center text-sm ${isModalVariant ? 'text-gray-300' : 'text-gray-500'}`}>
            {t('footerNote')}
          </p>
        </div>

        {!isModalVariant ? (
          <div className="mt-6 rounded-2xl border border-fest-gold/30 bg-fest-gold/10 p-4 backdrop-blur-xl">
            <p className="text-center text-sm font-medium text-fest-gold">{t('idHint')}</p>
          </div>
        ) : null}
      </div>
    </main>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-gradient-to-b from-fest-plate to-fest-charcoal">
      <Header />
      {content}

      <Footer />

      <WhatsAppSupportFab />
    </div>
  );
}
